class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => { if (event.target.id === 'CartDrawer-Overlay') this.close(); });
  }

  connectedCallback() {
    this.iconController?.abort();
    this.iconController = new AbortController();
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.iconController?.abort();
    clearTimeout(this.openTimer);
    clearTimeout(this.renderTimer);
    if (this.classList.contains('active') || this.openTimer) this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    }, { signal: this.iconController.signal });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    }, { signal: this.iconController.signal });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      if (!this.isConnected) return;
      this.classList.add('animate', 'active');
    });

    this.focusController?.abort();
    this.focusController = new AbortController();
    this.addEventListener(
      'transitionend',
      () => {
        if (!this.isConnected || !this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true, signal: this.focusController.signal },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.focusController?.abort();
    clearTimeout(this.openTimer);
    this.openTimer = null;
    clearTimeout(this.renderTimer);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (window.DBCommerce && !window.DBCommerce.validSections(parsedState, this)) {
      window.DBCommerce.error(this.querySelector('.drawer__inner'), 'The cart display could not be refreshed. Please view your cart.');
      return;
    }
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => { if (this.isConnected) this.open(); });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: this.dataset.sectionId || 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: this.closest('cart-drawer')?.dataset.sectionId || 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

// Position only native top-level theme blocks; nested children keep their parent order.
class CartDrawerExtra extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    if (!this.drawer) return;
    this.positionObserver?.disconnect();
    this.positionObserver = new MutationObserver(() => this.positionThemeBlocks());
    this.positionObserver.observe(this.drawer, { childList: true, subtree: true });
    queueMicrotask(() => { if (this.isConnected) this.positionThemeBlocks(); });
  }

  disconnectedCallback() {
    this.positionObserver?.disconnect();
  }

  positionThemeBlocks() {
    const scroll = this.drawer.querySelector('.db-drawer-scroll');
    const footer = this.drawer.querySelector('.drawer__footer');
    if (!scroll || !footer) return;
    const total = footer.querySelector('[data-db-type="cart_subtotals"]') || footer.querySelector('.cart-drawer__footer');
    const checkout = footer.querySelector('[data-db-type="cart_checkout-btn"]') || footer.querySelector('.cart__ctas');
    const targets = {
      top: [scroll, scroll.firstChild],
      bottom: [this.parentNode, this],
      footer: [footer, footer.firstChild],
      before_total: total && [total.parentNode, total],
      after_total: total && [total.parentNode, total.nextSibling],
      after_checkout: checkout && [checkout.parentNode, checkout.nextSibling],
    };
    // Gift position is its own two-value contract, including nested Body/Footer blocks.
    for (const gift of this.drawer.querySelectorAll('[data-db-cart-gift-position]')) {
      const inFooter = gift.dataset.dbCartGiftPosition === 'footer';
      const parent = inFooter
        ? footer.querySelector('.cart-drawer__legacy-footer') || footer
        : scroll.querySelector('.cart-drawer__body') || scroll;
      if (gift.parentNode === parent) continue;
      const subtotal = inFooter && (parent.querySelector('[data-db-type="cart_subtotals"]') || parent.querySelector('.cart-drawer__footer')?.closest('.db-drawer-block') || parent.querySelector('.cart-drawer__footer'));
      let anchor = subtotal || null;
      while (anchor && anchor.parentNode !== parent) anchor = anchor.parentNode;
      parent.insertBefore(gift, anchor);
    }
    for (const node of Array.from(this.children)) {
      const target = targets[node.dataset.dbCartPosition];
      if (target) target[0].insertBefore(node, target[1]);
    }
  }
}

if (!customElements.get('db-cart-extra')) customElements.define('db-cart-extra', CartDrawerExtra);
